/**
 * Aba "Outros" (GUPM-192) — agrupador de referências, fiel ao `renderMOUT` do V8.
 * Sub-abas na ordem do V8 (6158): 📞 Contatos (default; "Contatos de Suporte" até a C-75) + 📧 Comunicação
 * + 📄 Documentos + 🧩 Matriz RACI.
 *
 * Contatos de Suporte (GUPM-200): diretório por empresa com dados do banco `contacts`
 * — ver `components/ContatosSuporte.tsx`. PII nunca versionada (LGPD/GUPM-158).
 *
 * C-75: a sub-aba lista TODOS os contatos (emergência + suporte) e é o único lugar
 * de edição — só admin, ver `components/ContatosAba.tsx`. Gate espelhado na UI; o
 * gate real é a RLS de `contacts`.
 *
 * Matriz RACI (GUPM-192, fatia RACI): Rev.1 (Out/2026), rascunho — ver
 * `components/MatrizRaci.tsx`. Só RT e admin veem o chip e o conteúdo; operador
 * (vigilante) não, e perfil ainda não resolvido também não (`podeVerMatrizRaci`).
 * Gate de EXIBIÇÃO: o conteúdo é estático no bundle.
 */
import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { PageHeader } from '@/ui';
import { DOC_TIPO_COR, DOCS_REFERENCIA, MATRIZ_COMUNIC, podeVerMatrizRaci } from '@/domain/outros';
import ContatosAba from './components/ContatosAba';
import MatrizRaci from './components/MatrizRaci';

type OutrosTab = 'contatos' | 'comunic' | 'docs' | 'raci';

const TABS: { id: OutrosTab; label: string }[] = [
  { id: 'contatos', label: '📞 Contatos' },
  { id: 'comunic', label: '📧 Comunicação' },
  { id: 'docs', label: '📄 Documentos' },
  { id: 'raci', label: '🧩 Matriz RACI' },
];

function DocumentosView() {
  return (
    <div className="space-y-2">
      <p className="text-xs text-jf-muted">
        Documentos vigentes que fundamentam a operação, em ordem de precedência técnica. Verifique a
        revisão em vigor antes do uso.
      </p>
      {DOCS_REFERENCIA.map((d) => (
        <div
          key={d.cod}
          className="rounded-lg border border-jf-border bg-jf-surface p-3 border-l-[3px]"
          style={{ borderLeftColor: DOC_TIPO_COR[d.tipo] }}
        >
          <div className="text-sm font-semibold text-white">{d.doc}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-jf-muted">
            <span className="font-mono">{d.cod}</span>
            <span aria-hidden>·</span>
            <span>{d.rev}</span>
            <span
              className="ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold uppercase"
              style={{ color: DOC_TIPO_COR[d.tipo], background: `${DOC_TIPO_COR[d.tipo]}1a` }}
            >
              {d.tipo}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ComunicacaoView() {
  return (
    <div className="space-y-3">
      <p className="text-xs text-jf-muted">
        Matriz de comunicação — quem comunica o quê, a quem e em que prazo, por gatilho.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="text-jf-dim">
              <th className="px-2 py-1.5 font-bold">Gatilho</th>
              <th className="px-2 py-1.5 font-bold">Remetente</th>
              <th className="px-2 py-1.5 font-bold">Destinatários</th>
              <th className="px-2 py-1.5 font-bold">Prazo</th>
            </tr>
          </thead>
          <tbody>
            {MATRIZ_COMUNIC.map((m) => (
              <tr key={m.gatilho} className="border-t border-jf-border align-top">
                <td className="px-2 py-1.5 font-semibold text-white">{m.gatilho}</td>
                <td className="px-2 py-1.5 text-jf-muted">{m.remetente}</td>
                <td className="px-2 py-1.5 text-jf-muted">{m.destinatarios}</td>
                <td className="px-2 py-1.5 text-jf-muted">{m.prazo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-jf-dim">
        Modelos de e-mail padrão (E-01…E-10) — em breve nesta sub-aba (GUPM-192).
      </p>
    </div>
  );
}

export default function Outros() {
  const [escolhida, setTab] = useState<OutrosTab>('contatos');
  const { profile } = useAuth();
  const verRaci = podeVerMatrizRaci(profile);
  // Perfil que perde a matriz com ela aberta (troca de sessão) volta para a aba
  // inicial — sem isso a tela ficava vazia e sem chip ativo. O conteúdo nunca vaza:
  // `tab` só é 'raci' quando `verRaci`.
  const tab: OutrosTab = escolhida === 'raci' && !verRaci ? 'contatos' : escolhida;
  const abas = TABS.filter((t) => t.id !== 'raci' || verRaci);
  return (
    <div>
      <PageHeader title="📚 Outros — Referências e Responsabilidades" />

      <div className="mb-3 flex flex-wrap gap-2">
        {abas.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            aria-pressed={tab === t.id}
            className={`rounded-full border px-3 py-1 text-xs font-bold transition ${
              tab === t.id
                ? 'border-jf-yellow bg-jf-yellow/15 text-jf-yellow'
                : 'border-jf-border text-jf-muted hover:text-white'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'contatos' && (
        <ContatosAba admin={profile === 'admin'} />
      )}
      {tab === 'comunic' && <ComunicacaoView />}
      {tab === 'docs' && <DocumentosView />}
      {tab === 'raci' && <MatrizRaci />}
    </div>
  );
}
